import React from 'react';
import { motion, useReducedMotion } from 'motion/react';

/**
 * FadeIn component - Animates children with fade-in effect
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to animate
 * @param {number} props.delay - Delay before animation starts (default: 0)
 * @param {number} props.duration - Animation duration (default: 0.5)
 * @param {string} props.direction - Animation direction: 'up', 'down', 'left', 'right', 'none' (default: 'up')
 * @param {number} props.distance - Distance to travel in pixels (default: 20)
 * @param {string} props.className - Additional CSS classes
 */
const FadeIn = ({
  children,
  delay = 0,
  duration = 0.5,
  direction = 'up',
  distance = 20,
  className = '',
  play = true,
  instant = false,
  forceVisible = false,
  ...props
}) => {
  const reducedMotion = useReducedMotion();
  const snapVisible = instant || forceVisible || reducedMotion;
  const directions = {
    up: { y: distance },
    down: { y: -distance },
    left: { x: distance },
    right: { x: -distance },
    none: {}
  };

  const initial = {
    opacity: 0,
    ...directions[direction]
  };

  const animate = {
    opacity: 1,
    x: 0,
    y: 0
  };

  const from = snapVisible ? animate : initial;
  const to = play || snapVisible ? animate : initial;

  return (
    <motion.div
      initial={from}
      animate={to}
      transition={{
        duration: snapVisible ? 0 : duration,
        delay: snapVisible ? 0 : play ? delay : 0,
        ease: [0.25, 0.1, 0.25, 1.0]
      }}
      className={`${className}${snapVisible && play ? " fade-in-static" : ""}`.trim()}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export default FadeIn;
